import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  TextField,
  Select,
  ChoiceList,
  FormLayout,
  Text,
  InlineError,
} from '@shopify/polaris';
import { usersService } from '../../services/usersService';

const AddUserModal = ({ active, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    group_id: '',
    country_access_mode: 'selected',
    allowed_countries: ['EG'],
    default_country: 'EG',
  });

  const [groups, setGroups] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);

  // Load groups on mount
  useEffect(() => {
    if (active) {
      loadGroups();
    }
  }, [active]);

  const loadGroups = async () => {
    try {
      setLoadingGroups(true);
      const groupsData = await usersService.getAllGroups();

      // Format groups for Select component
      const groupOptions = groupsData.map(group => ({
        label: group.name,
        value: group._id || group.id,
      }));

      setGroups([
        { label: 'Select a group...', value: '' },
        ...groupOptions,
      ]);
    } catch (error) {
      console.error('Failed to load groups:', error);
      setErrors({ general: 'Failed to load groups. Please try again.' });
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleChange = useCallback((field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  }, [errors]);

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Name validation
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Name is required';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Group validation
    if (!formData.group_id) {
      newErrors.group_id = 'Please select a group';
    }

    if (formData.country_access_mode === 'selected' && formData.allowed_countries.length === 0) {
      newErrors.allowed_countries = 'Select at least one country';
    }

    if (formData.country_access_mode === 'selected' && !formData.allowed_countries.includes(formData.default_country)) {
      newErrors.default_country = 'Default country must be one of selected countries';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Create user data without confirmPassword
      const { confirmPassword, ...userData } = formData;

      await usersService.createUser(userData);

      // Reset form
      setFormData({
        email: '',
        name: '',
        password: '',
        confirmPassword: '',
        group_id: '',
        country_access_mode: 'selected',
        allowed_countries: ['EG'],
        default_country: 'EG',
      });
      setErrors({});

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to create user:', error);
      setErrors({
        general: error.message || 'Failed to create user. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      email: '',
      name: '',
      password: '',
      confirmPassword: '',
      group_id: '',
      country_access_mode: 'selected',
      allowed_countries: ['EG'],
      default_country: 'EG',
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      open={active}
      onClose={handleCancel}
      title="Add New User"
      primaryAction={{
        content: 'Create User',
        onAction: handleSubmit,
        loading: loading,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: handleCancel,
        },
      ]}
    >
      <Modal.Section>
        <FormLayout>
          {errors.general && (
            <InlineError message={errors.general} fieldID="general-error" />
          )}

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange('email')}
            error={errors.email}
            autoComplete="email"
            placeholder="user@example.com"
          />

          <TextField
            label="Name"
            value={formData.name}
            onChange={handleChange('name')}
            error={errors.name}
            autoComplete="name"
            placeholder="John Doe"
          />

          <Select
            label="Group/Role"
            options={groups}
            value={formData.group_id}
            onChange={handleChange('group_id')}
            error={errors.group_id}
            disabled={loadingGroups}
            helpText="Select the user's group to determine their permissions"
          />

          <Select
            label="Country Access"
            value={formData.country_access_mode}
            onChange={handleChange('country_access_mode')}
            options={[
              { label: 'Selected countries', value: 'selected' },
              { label: 'All countries', value: 'all' },
            ]}
            helpText="Choose whether the user can access all countries or specific ones"
          />

          {formData.country_access_mode === 'selected' && (
            <ChoiceList
              title="Allowed Countries"
              allowMultiple
              choices={[
                { label: 'Egypt (EG)', value: 'EG' },
                { label: 'UAE', value: 'UAE' },
              ]}
              selected={formData.allowed_countries}
              onChange={(value) => {
                const nextAllowed = value.length > 0 ? value : ['EG'];
                setFormData((prev) => ({
                  ...prev,
                  allowed_countries: nextAllowed,
                  default_country: nextAllowed.includes(prev.default_country) ? prev.default_country : nextAllowed[0],
                }));
              }}
              error={errors.allowed_countries}
            />
          )}

          <Select
            label="Default Country"
            value={formData.default_country}
            onChange={handleChange('default_country')}
            options={(formData.country_access_mode === 'all'
              ? ['EG', 'UAE']
              : formData.allowed_countries
            ).map((country) => ({ label: country, value: country }))}
            error={errors.default_country}
            helpText="Used as the initial country context after login"
          />

          <TextField
            label="Password"
            type="password"
            value={formData.password}
            onChange={handleChange('password')}
            error={errors.password}
            autoComplete="new-password"
            placeholder="Minimum 8 characters"
            helpText="Password must be at least 8 characters long"
          />

          <TextField
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange('confirmPassword')}
            error={errors.confirmPassword}
            autoComplete="new-password"
            placeholder="Re-enter password"
          />

          <Text variant="bodySm" tone="subdued">
            The user will be able to login immediately with these credentials.
          </Text>
        </FormLayout>
      </Modal.Section>
    </Modal>
  );
};

export default AddUserModal;
